import React from 'react';
import type { FmLegendEntry } from '../../types/fm-legend';
import { ColorSwatch } from './ColorSwatch';
import { paletteEvidenceRoleOpacity } from './charts/palette';

export interface FmLegendProps {
  entries: FmLegendEntry[];
  direction?: 'wrap' | 'column';
  gap?: number;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  style?: React.CSSProperties;
  /**
   * Test id for the legend root. Declared explicitly because this component
   * does not spread rest props — without the declaration a consumer's
   * `data-testid` is silently dropped and its e2e contract breaks.
   */
  'data-testid'?: string;
}

export const FmLegend: React.FC<FmLegendProps> = ({
  entries,
  direction = 'wrap',
  gap = 12,
  className,
  style,
  'data-testid': dataTestId,
}) => (
  <div
    className={['fm-legend', direction === 'column' && 'fm-legend--column', className]
      .filter(Boolean)
      .join(' ')}
    style={{ gap, ...style }}
    data-testid={dataTestId}
  >
    {entries.map((entry) => (
      <div
        key={`${entry.color}__${entry.label}`}
        className="fm-legend__entry"
        data-role={entry.role}
        data-testid={entry.testId}
      >
        {entry.pattern ? (
          <span
            className={`fm-legend__line fm-legend__line--${entry.pattern}`}
            aria-hidden="true"
            style={{
              borderTopColor: entry.color,
              opacity: paletteEvidenceRoleOpacity(entry.role),
            }}
          />
        ) : (
          <ColorSwatch
            color={entry.color}
            size={10}
            className="fm-legend__swatch"
            style={{ opacity: paletteEvidenceRoleOpacity(entry.role) }}
          />
        )}
        <span className="fm-legend__label">{entry.label}</span>
        {entry.value !== undefined ? <span className="fm-legend__value">{entry.value}</span> : null}
      </div>
    ))}
  </div>
);

FmLegend.displayName = 'FmLegend';
